<template>
<LayoutSearchFieldVertical
  display-variant="list"
  :is-show="!!selected"
>
  <template #tabList>
    <div class="group-parent">
      <button
        class="group-button"
        v-for="group in typeData"
        :class="{
          'is-selected': toRaw(selected) === group,
          'not-selected': selected != null && toRaw(selected) !== group,
        }"
        @click="selectGroup(group)"
        :key="group.label.ja"
      >
        <span class="group-label"
          >{{ ls(group.label)
          }}</span>
        <template v-if="facet">&nbsp;({{ nums(group.count) }})</template>
        <BaseIcon :name="toRaw(selected) === group ? 'mdi/menu-up' : 'mdi/menu-down'" />
      </button>
    </div>
  </template>
    <template #default v-if="selected !== null">
      <div class="facet-buttons">
        <button
          class="fb-button"
          v-for="group in selected.children"
          :class="{
            'is-0': count(group.key) === 0,
            'is-exclude': exclude,
            'is-selected': isSelected('+' + group.key),
          }"
          @click="select(group)"
          :key="group.key"
        >
          <span class="fb-label">{{ display(group) }}</span>
          <span class="fb-count" :title="num(count(group.key))" v-if="facet">{{
            facetNum(count(group.key))
          }}</span>
        </button>
      </div>
    </template>

    <template #options v-if="selected !== null">
      <div class="exclude-sw">
        <BaseToggleSwitch class="is-red" v-model="exclude">{{
          lss('除外条件を指定', 'Exclude')
        }}</BaseToggleSwitch>
      </div>
    </template>
</LayoutSearchFieldVertical>
</template>

<script lang="ts" setup>
import { useSearchFieldBase } from '~/components/search/search-field/useBaseSearchField'
import type { SearchFieldBaseProps } from '~/components/search/search-field/useBaseSearchField'

const { ls, lss, lst, num, nums } = useI18n()
const { $getCodeRdfType } = useNuxtApp()

interface Props extends SearchFieldBaseProps {
}
const props = withDefaults(defineProps<Props>(), {
})

const { facet, facetNum } = useSearchFieldBase(props)

const rdfTypeCode: Code = $getCodeRdfType()

const RDF_TYPE_TREE = [
  {
    label: { ja: '本・文書', en: 'Books and documents' },
    children: [
      'book',
      'periodical',
      'article',
      'map',
      'ancdoc',
      'poetry',
      'poster',
      'postcard',
      'offdoc',
      'docs',
    ],
  },
  {
    label: { ja: '地域資料', en: 'Regional materials' },
    children: ['archeology', 'customs', 'site', 'map', 'stone'],
  },
  {
    label: { ja: '工芸', en: 'Crafts' },
    children: [
      'weaponry',
      'woodw',
      'metal',
      'ceramic',
      'lacquer',
      'daw',
      'craft',
    ],
  },
  {
    label: { ja: '建築・芸術', en: 'Architecture and arts' },
    children: [
      'architecture',
      'paint',
      'sculpture',
      'photo',
      'movie',
      'mediaart',
      'performance',
      'poster',
      'mscore',
      'exhibition',
      'arts',
    ],
  },
  {
    label: { ja: 'メディア', en: 'Media' },
    children: [
      'video',
      'recording',
      'photo',
      'broadcast',
      'game',
      'anime',
      'website',
      'ematerial',
      '3d',
      'media',
    ],
  },
  {
    label: { ja: '自然科学・技術', en: 'Science and technology' },
    children: ['mineral', 'natural'],
  },
  {
    label: { ja: 'その他', en: 'Others' },
    children: ['other', 'dataset', 'org'],
  },
]

interface Group {
  label: LangString
  key?: string
  count?: number
  children?: Group[]
}

const getRdfTypeCode = (code: string) => {
  return rdfTypeCode.values.filter(v => v.code === code).pop()
}

const selected = ref<Group>(null)

const typeData = computed(() => {
  return RDF_TYPE_TREE.map(type => {
    return {
      facet: facet.value,
      label: type.label,
      count: type.children
        .map(code => count(code))
        .reduce((prev, current) => prev + current, 0),
      children: [
        ...type.children.map(code => {
          return {
            label: getRdfTypeCode(code)?.value,
            key: code,
          }
        }),
      ],
    }
  })
})

const exclude = ref(false)

const count = (code) => {
  if (facet.value) {
    return facet.value.counts[code] || 0
  }
  return 0
}

const selectGroup = (group: Group) => {
  if (toRaw(selected.value) === group) {
    selected.value = null
  } else {
    selected.value = group
  }
}

const select = (group: Group) => {
  let code = group.key
  if (exclude.value) code = '-' + code
  else code = '+' + code
  if (isSelected(code)) {
    props.ss.removeFilter(props.field.key, [code])
  } else {
    props.ss.addFilter(props.field.key, [code])
  }
}

const isSelected = (code: string) => {
  return props.ss.state.filter[props.field.key]?.includes(code)
}

const display = (group: Group) => {
  let v = ls(group.label)
  if (exclude.value && count(group.key) !== 0)
    v = lst({ ja: '{}を除外', en: 'Exclude {}' }, v)
  return v
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

  .group-parent {
    display: flex;
    align-items: stretch;

    @include is-mobile {
      flex-wrap: wrap;
    }
  }

  .group-button {
    display: flex;
    align-items: center;
    padding: 12px;
    text-align: left;
    font-size: 0.875rem;
    cursor: pointer;

    @include is-mobile {
      flex: 0 0 50%;
    }

    @include is-tablet {
      border-right: 1px solid $jps-gray-300;
    }
  }

  .not-selected {
    @include is-tablet {
      border-bottom: 1px solid $jps-gray-300;
    }
  }

  :deep(.layout-search-field-flex) {
    @include is-mobile {
      border-top: 1px solid $jps-gray-300;
    }
  }
</style>
